import { SolutionData } from "@/globals/helpers/worksheetHandler";
import {
  IAnswerWrapper,
  IOpenEndedAnswer,
  useUserAnswersData,
  IClosedEndedAnswer,
  IAssessment,
  Reviewer,
  EvaluationScale,
  IWorksheetAnswerStore,
} from "@/modules/a.worksheets/stores/wksUserAnswersDataStore";
import {
  IActivity,
  ProblemType,
  useWorksheetData,
} from "@/modules/a.worksheets/stores/worksheetDataStore";
import dataTreeManager from "@/persistentStorage/wks_storage/dataTreeManager";
import { writeFile } from "@/helpers/filesReadWrite";
import { toRefs } from "vue";
import { useAccountDataUser } from "@/modules/5.account/stores/accountDataStore";
import { isLocalIFile } from "@/helpers/api";
import Delta from 'quill-delta';
import { IOfflineScore, scoreOfflineAnswer } from "@/modules/k.offlineMode/1.worksheets/helpers/offlineScoring";

export function useOfflineAnswerUtils() {
  const _answerStore: ReturnType<typeof useUserAnswersData> =
    useUserAnswersData();
  const _wksStore: ReturnType<typeof useWorksheetData> = useWorksheetData();
  const { uuid } = toRefs(useAccountDataUser());

  function emptyWrappedAnswer(activity: IActivity, evidences?: Delta | null): IAnswerWrapper {
    return {
      uuid: "unset-uuid",
      solved_at: Date.now(),
      problem: activity.problem!.uuid,
      teacher_solution: null,
      answer: {
        drag_drop: null,
        multiple_choice: null,
        sequence_ordering: null,
        image_labels: null,
        words_select: null,
        match_options: null,
        open_ended: null,
        close_ended: null,
        table_fill: null,
        ...(evidences !== undefined
          ? { evidences: evidences ? JSON.parse(JSON.stringify(evidences)) : null }
          : {}),
      },
    };
  }

  function scoreFor(activity: IActivity, studentAnswer: unknown): IOfflineScore | null {
    const problem = activity.problem;
    if (!problem) return null;
    const teacherSolution =
      _answerStore.getAnswer(problem.uuid)?.teacher_solution ?? problem.teacher_solution ?? [];
    return scoreOfflineAnswer(problem, teacherSolution, studentAnswer);
  }

  function selfAssessment(score: IOfflineScore): IAssessment {
    return {
      who_reviewed: Reviewer.SELF,
      evaluation_scale: EvaluationScale.QUANTITATIVE,
      grade: String(score.grade),
      suffix: "",
    };
  }

  function persistAnswer(wrappedAnswer: IAnswerWrapper) {
    _answerStore.setAnswer(wrappedAnswer);

    const wksUUID = _wksStore.uuid;
    const newSolvedRatio = calcSolvedRatio();

    dataTreeManager.updateAnswers(uuid.value, wksUUID, _answerStore.$state, {
      solvedRatio: newSolvedRatio,
      preprocess: false,
      isDataLocale: true, // Because we are operating on a local data level
    });
  }

  async function setAnswer(
    taskUUID: string,
    actUUID: string,
    data: string | boolean | File[] | Delta,
    type: SolutionData,
    evidences?: Delta | null
  ) {
    const activity = _wksStore.getActivity(taskUUID, actUUID);
    const problemType = activity?.problem?.problem_type;

    const problemUUID = activity?.problem?.uuid;
    if (!problemUUID) {
      console.error("Problem UUID is not set. Cannot set answer.");
      return;
    }

    const wrappedAnswer = emptyWrappedAnswer(activity, evidences);

    if (problemType === ProblemType.OPEN && typeof data !== "boolean") {
      if (typeof data === "string" || Array.isArray(data) || (looksLikeDelta(data) && type === 'text')) {
        const openEndedAnswer = await constructOpenAnswer(data, type);
        // if(!openEndedAnswer) throw new Error('Custom Error!. Open-ended answer is not constructed properly');
        wrappedAnswer.answer!.open_ended = openEndedAnswer;
      } else {
        // throw new Error(
        //   "Custom Error!. Data must be a string or an array of File objects for open-ended problems. Typeof data: " +
        //     typeof data
        // );
      }
    } else {
      const closedValue = typeof data === "boolean" ? (data ? "True" : "False") : (data as unknown as string);
      const closedEndedAnswer = constructClosedAnswer(closedValue);
      const score = scoreFor(activity, closedValue);
      if (score) {
        closedEndedAnswer.correct_answer = activity.problem!.close_ended?.correct_answer ?? "";
        closedEndedAnswer.correct_answer_pos = activity.problem!.close_ended?.correct_answer_pos ?? 0;
        closedEndedAnswer.n_options = activity.problem!.close_ended?.n_options ?? 0;
        closedEndedAnswer.is_correct = score.isCorrect;
        closedEndedAnswer.answer_assessment = [selfAssessment(score)];
      }
      wrappedAnswer.answer!.close_ended = closedEndedAnswer;
    }

    persistAnswer(wrappedAnswer);
  }

  const INTERACTIVE_ANSWER_FIELD: Record<string, string> = {
    [ProblemType.WORDS_SELECTION]: "words_select",
    [ProblemType.WORD_DRAG_DROP]: "drag_drop",
    [ProblemType.MATCH_CASE]: "match_options",
    [ProblemType.IMAGE_LABELING]: "image_labels",
    [ProblemType.TABLE_FILL]: "table_fill",
    [ProblemType.SEQUENCE_ORDERING]: "sequence_ordering",
    [ProblemType.MULTIPLE_CHOICE]: "multiple_choice",
  };

  function setInteractiveAnswer(
    taskUUID: string,
    actUUID: string,
    studentAnswer: unknown,
    evidences?: Delta | null
  ) {
    const activity = _wksStore.getActivity(taskUUID, actUUID);
    const problemUUID = activity?.problem?.uuid;
    const field = activity?.problem?.problem_type
      ? INTERACTIVE_ANSWER_FIELD[activity.problem.problem_type]
      : undefined;

    if (!problemUUID || !field) {
      console.error("Interactive answer: problem not found or type not supported", activity?.problem?.problem_type);
      return;
    }

    const wrappedAnswer = emptyWrappedAnswer(activity, evidences);
    const score = scoreFor(activity, studentAnswer);
    (wrappedAnswer.answer as any)[field] = score
      ? { student_answer: score.studentAnswer, answer_assessment: [selfAssessment(score)] }
      : { student_answer: studentAnswer };

    persistAnswer(wrappedAnswer);
  }

  async function constructOpenAnswer(
    data: string | Delta | File[],
    type: SolutionData
  ): Promise<IOpenEndedAnswer> {
    let input_type = 1;
    let answer_files = [];

    switch (type) {
      case "image":
        input_type = 1;
        break;
      case "video":
        input_type = 2;
        break;
      case "audio":
        input_type = 3;
        break;
      case "pdf":
        input_type = 4;
        break;
      case "text":
        input_type = 5;
        break;
      case "document":
        input_type = 6;
        break;
    }

    if (looksLikeDelta(data) && type === 'text') {
      answer_files = [
        {
          uuid: "unset-uuid",
          order: 0,
          file: null,
          textfield: null,
          textrtf: data,
        },
      ];
    }
    else if (typeof data === "string") {
      answer_files = [
        {
          uuid: "unset-uuid",
          order: 0,
          file: null,
          textfield: data,
          textrtf: null,
        },
      ];
    } else if (Array.isArray(data)) {
      for (let i = 0; i < data.length; i++) {
        const file = data[i];
        const { uri } = await writeFile("wks_files/" + file.name, file);
        answer_files.push({
          uuid: "unset-uuid",
          order: i,
          file: {
            filename: file.name,
            filetype: file.type,
            filepath: uri,
            isToUpdate: false,
            isLocal: true,
          },
          textfield: null,
          textrtf: null,
        });
      }
    }

    const open_ended: IOpenEndedAnswer = {
      uuid: "data.uuid",
      input_type: input_type,
      answer_files: answer_files,
      answer_assessment: [],
    };
    return open_ended;
  }

  function looksLikeDelta(obj: unknown): obj is Delta {
    return typeof obj === 'object'
      && obj !== null
      && !Array.isArray(obj)
      && 'ops' in obj;
  }

  //rever
  function constructClosedAnswer(data: string): IClosedEndedAnswer {
    const closed_ended: IClosedEndedAnswer = {
      student_answer: data,
      correct_answer: "",
      correct_answer_pos: 0,
      is_correct: false,
      n_options: 0
    };
    return closed_ended;
  }

  function setSelfAssessment(
    taskUUID: string,
    actUUID: string,
    assessment: string
  ) {
    const activity = _wksStore.getActivity(taskUUID, actUUID);
    const problemUUID = activity?.problem?.uuid;

    if (!problemUUID) {
      console.error("Problem UUID is not set. Cannot set answer.");
      return;
    }

    const answer = _answerStore.answers.find((a) => a.problem === problemUUID);
    if (!answer) {
      console.error("Answer not found");
      return;
    } else if (!answer.answer?.open_ended) {
      console.error("Open ended answer not found");
      return;
    }

    const newAssessment: IAssessment = {
      who_reviewed: Reviewer.SELF,
      evaluation_scale: activity.problem!.open_ended!.evaluation_scale,
      grade: assessment,
      suffix: "",
    };
    answer.answer.open_ended.answer_assessment.push(newAssessment);

    const wksUUID = _wksStore.uuid;

    console.log("_answerStore.$state", _answerStore.$state);
    dataTreeManager.updateAnswers(uuid.value, wksUUID, _answerStore.$state, {
      preprocess: false,
      isDataLocale: true,
    });
  }

  function setAnswerData(data: IWorksheetAnswerStore) {
    _answerStore.$patch(data);
  }

  function calcSolvedRatio(): number {
    let total = 0;
    let solved = 0;

    _wksStore.tasks.forEach((task) => {
      task.task.activities.forEach((activity) => {
        total++;
        if (isCompleted(activity)) solved++;
      });
    });
    return solved / total;
  }
  function isCompleted(activity: IActivity): boolean {
    const problem = activity?.problem;
    const problemUUID = problem?.uuid;
    if (!problemUUID) return false;
    return _answerStore.isAnswer(problemUUID);
  }
  async function writeFiles(files: File[]) {
    //
  }

  return {
    setAnswer,
    setInteractiveAnswer,
    setSelfAssessment,
    setAnswerData,
  };
}
